import { useEffect, useState, useCallback, useRef } from "react";
import { useAuth } from "@/App";
import { BookingPaymentDialog, PaymentStatusChip, usePaymentStatuses, actionLabel } from "@/components/BookingPaymentPanel";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useNavigate } from "react-router-dom";
import {
  ChefHat, Plus, Edit, Trash2, Search, Download, Loader2, Mail, CheckCircle2, MessageSquare, BookHeart,
} from "lucide-react";
import { toast } from "sonner";
import MenuPicker from "@/components/booking/MenuPicker";
import { MenuPicker as MasterSearch } from "@/components/corporate-invoices/MenuPicker";
import { PackageCards, MenuSelector, ServicesPicker, SECTIONS, pkgPrice, selectionGaps } from "@/components/catering/PackageBuilder";
import { CostSummary } from "@/components/bi-shell/CostSummary";
import WhatsAppDialog from "@/components/booking/WhatsAppDialog";
import { isInternationalCenter } from "@/lib/api";
import { resolveCurrency } from "@/utils/money";

const API = process.env.REACT_APP_BACKEND_URL;

const STATUSES = ["Quote", "Confirmed", "Cancelled", "Completed"];
const PAY_STATUSES = ["Pending", "Partial", "Paid"];
const MENU_CATS = ["Starters", "Main Course", "Roti / Bread", "Rice / Biryani", "Dal", "Vegetable", "Sweet / Dessert", "Salad / Raita"];

const today = () => new Date().toISOString().split("T")[0];
const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

const blankForm = {
  customer_name: "", phone: "", email: "",
  billing_address: "", delivery_address: "",
  order_date: today(), event_date: today(), event_time: "",
  guest_count: 50, occasion: "", package_name: "",
  per_person_rate: 0,
  menu_categories: {}, beverages: [], add_ons: [],
  crockery: "", staffing: "",
  transport_charges: 0,
  subtotal: 0, gst: 0, total: 0,
  advance_paid: 0, balance_due: 0,
  package_id: "", menu_selections: {}, crockery_service: null, staff_service: null,
  food_total: 0, discount: 0, gst_mode: "exclusive", gst_rate: 0,
  confirmation_status: "Quote", payment_status: "Pending",
  assigned_coordinator: "", quote_notes: "", remarks: "",
  center: "",
};

export default function CateringOrders({ wsCenter }) {
  const { session } = useAuth();
  const navigate = useNavigate();
  const token = session?.token;
  const [payRow, setPayRow] = useState(null);
  const isAdmin = session?.is_super_admin || session?.is_admin;
  const userCenter = session?.center || "";

  const [rows, setRows] = useState([]);
  const [payMap, reloadPay] = usePaymentStatuses("CATERING", rows.map((x) => x.id), session?.token);
  const [loading, setLoading] = useState(false);
  const [centers, setCenters] = useState([]);
  const [menuMaster, setMenuMaster] = useState([]);
  const [filters, setFilters] = useState({
    center: isAdmin ? "" : userCenter,
    from_date: "", to_date: "", status: "", search: "",
  });
  const [showDlg, setShowDlg] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...blankForm, center: userCenter });
  const [waRow, setWaRow] = useState(null);
  const [web, setWeb] = useState(null);
  const [masterItems, setMasterItems] = useState([]);

  useEffect(() => {
    if (!token) return;
    fetch(`${API}/api/sales/centers-list`).then(r => r.ok && r.json()).then((d) => {
      if (d?.centers) setCenters(d.centers);
    });
    fetch(`${API}/api/bookings/ext/catering/website-packages`, {
      method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ token }),
    }).then(r => r.ok && r.json()).then((d) => { if (d?.packages) setWeb(d); });
    fetch(`${API}/api/bookings/ext/menu/list`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token }),
    }).then(r => r.ok && r.json()).then((d) => {
      if (d?.items) setMenuMaster(d.items);
    });
  }, [token]);

  useEffect(() => {
    if (wsCenter !== undefined && isAdmin) setFilters((f) => (f.center === wsCenter ? f : { ...f, center: wsCenter }));
  }, [wsCenter, isAdmin]);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/bookings/ext/catering/list`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          token,
          centers: filters.center ? [filters.center] : [],
          from_date: filters.from_date || null,
          to_date: filters.to_date || null,
          status: filters.status || null,
          search: filters.search || null,
        }),
      });
      if (res.ok) setRows((await res.json()).rows || []);
    } finally { setLoading(false); }
  }, [token, filters]);

  useEffect(() => { load(); }, [load]);

  // Canonical inclusive-carve GST: India 5% / International 10%
  const recompute = (f) => {
    if (f.gst_mode === "exclusive") {
      // Website quotation: package × guests + services + extras + transport − discount, GST added on top
      const intl = isInternationalCenter(f.center, centers);
      const food = round2(Number(f.per_person_rate || 0) * Number(f.guest_count || 0));
      const cs = f.crockery_service ? { ...f.crockery_service, total: round2(Number(f.crockery_service.hours || 0) * Number(f.crockery_service.rate || 0)) } : null;
      const ss = f.staff_service ? { ...f.staff_service, total: round2(Number(f.staff_service.count || 0) * Number(f.staff_service.hours || 0) * Number(f.staff_service.rate || 0)) } : null;
      let extras = 0;
      [...(f.beverages || []), ...(f.add_ons || [])].forEach((r) => { extras += Number(r.amount) || 0; });
      const subtotal = round2(Math.max(food + (cs?.total || 0) + (ss?.total || 0) + extras + (Number(f.transport_charges) || 0) - (Number(f.discount) || 0), 0));
      const rate = intl ? 10 : 5;
      const gst = round2(subtotal * rate / 100);
      const total = round2(subtotal + gst);
      return { ...f, food_total: food, crockery_service: cs, staff_service: ss, subtotal, gst, gst_rate: rate, total,
        balance_due: round2(total - (Number(f.advance_paid) || 0)) };
    }
    let lineTotal = 0;
    Object.values(f.menu_categories || {}).forEach((rows) => {
      rows.forEach((r) => { lineTotal += Number(r.amount) || 0; });
    });
    (f.beverages || []).forEach((r) => { lineTotal += Number(r.amount) || 0; });
    (f.add_ons || []).forEach((r) => { lineTotal += Number(r.amount) || 0; });
    // If line items absent, fall back to per_person × guest_count
    if (lineTotal === 0 && Number(f.per_person_rate) > 0) {
      lineTotal = Number(f.per_person_rate) * Number(f.guest_count || 0);
    }
    const transport = Number(f.transport_charges) || 0;
    const total = round2(lineTotal + transport);
    const isIntl = isInternationalCenter(f.center, centers);
    const rate = isIntl ? 0.10 : 0.05;
    const gst = round2(total - total / (1 + rate));
    const subtotal = round2(total - gst);
    const balance_due = round2(total - (Number(f.advance_paid) || 0));
    return { ...f, subtotal, gst, total, balance_due };
  };

  // Auto-recompute on key changes
  useEffect(() => {
    setForm((f) => recompute(f));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.menu_categories, form.beverages, form.add_ons,
      form.per_person_rate, form.guest_count, form.transport_charges,
      form.advance_paid, form.center, centers, form.gst_mode, form.discount,
      form.crockery_service?.hours, form.crockery_service?.count, form.staff_service?.count, form.staff_service?.hours,
      !!form.crockery_service, !!form.staff_service]);

  const open = (row) => {
    if (row) {
      setEditing(row);
      setForm(recompute({ ...blankForm, gst_mode: "", ...row }));
    } else {
      setEditing(null);
      setForm(recompute({ ...blankForm, center: wsCenter || userCenter }));
    }
    setShowDlg(true);
  };

  const save = async () => {
    if (!form.customer_name || !form.phone) { toast.error("Customer name and phone required"); return; }
    if (!form.center) { toast.error("Center required"); return; }
    const url = editing
      ? `${API}/api/bookings/ext/catering/update/${editing.id}`
      : `${API}/api/bookings/ext/catering/create`;
    const payload = recompute(form);
    if (payload.gst_mode === "exclusive" && !payload.package_id) { toast.error("Select a catering package"); return; }
    const gaps = selectionGaps(webPkgs.find((p) => p.id === payload.package_id), payload.menu_selections);
    if (gaps.length) toast.warning(`Menu not complete: ${gaps.map(([l, n, need]) => `${l.split(" (")[0]} ${n}/${need}`).join(", ")}`);
    const res = await fetch(url, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token, ...payload }),
    });
    if (res.ok) {
      toast.success(editing ? "Updated" : "Catering order created");
      window.dispatchEvent(new Event("bi-refresh"));
      setShowDlg(false); load();
    } else {
      const e = await res.json().catch(() => ({}));
      toast.error(e.detail || "Failed");
    }
  };

  const del = async (row) => {
    if (!window.confirm(`Delete catering ${row.id}?`)) return;
    const res = await fetch(`${API}/api/bookings/ext/catering/delete/${row.id}`, {
      method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ token }),
    });
    if (res.ok) { toast.success("Deleted"); load(); }
  };

  const downloadPdf = async (row) => {
    const res = await fetch(`${API}/api/bookings/ext/catering/pdf/${row.id}`, {
      method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ token }),
    });
    if (!res.ok) { toast.error("PDF failed"); return; }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `catering_${row.id}.pdf`;
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
  };

  const sendQuote = async (row) => {
    if (!row.email) {
      const m = window.prompt("Customer email not set on this order. Enter email to send to:");
      if (!m) return;
      row = { ...row, email: m };
    }
    const t = toast.loading(`Emailing quote to ${row.email}...`);
    try {
      const res = await fetch(`${API}/api/bookings/ext/catering/send-quote/${row.id}`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, to_email: row.email }),
      });
      if (res.ok) {
        const d = await res.json();
        toast.success(`Quote sent to ${d.to} (${d.size_kb} KB)`, { id: t });
        load();
      } else {
        const e = await res.json().catch(() => ({}));
        toast.error(e.detail || "Send failed", { id: t });
      }
    } catch (err) {
      toast.error("Network error", { id: t });
    }
  };

  const masterByCat = menuMaster.reduce((acc, m) => {
    (acc[m.category] = acc[m.category] || []).push(m); return acc;
  }, {});

  const openMemoryBox = (row) => {
    try {
      localStorage.setItem('mbox_prefill', JSON.stringify({
        source: 'Catering Order',
        center: row.center || userCenter,
        guest_name: row.customer_name || '',
        mobile: row.phone || '',
        email: row.email || '',
        event_date: row.event_date || today(),
        order_number: row.id || '',
        occasion: 'Catering Event',
        celebration_for: row.occasion || '',
      }));
      navigate('/ad-creator?tab=memory-box');
    } catch (e) {
      toast.error('Could not prefill Memory Box');
    }
  };

  const isIntl = isInternationalCenter(form.center, centers);
  const _coCenterDoc = (centers || []).find(c => c?.code === form.center);
  const currency = _coCenterDoc ? resolveCurrency(_coCenterDoc).symbol : (isIntl ? "$" : "₹");
  const gstLabel = isIntl ? "10% GST (incl.)" : "5% GST (incl.)";
  const webMode = form.gst_mode === "exclusive";
  const webPkgs = (web?.packages?.[isIntl ? "australia" : "india"]) || [];
  const curPkg = webPkgs.find((p) => p.id === form.package_id);
  const rates = { crockery: web?.service_rates?.crockery_per_hour?.[isIntl ? "australia" : "india"] ?? 0, staff: web?.service_rates?.staff_per_person_hour?.[isIntl ? "australia" : "india"] ?? 0 };
  useEffect(() => {
    if (!token || !form.center || !showDlg) return;
    fetch(`${API}/api/bookings/ext/catering/menu-master-items`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ token, center: form.center }) })
      .then(r => r.ok && r.json()).then((d) => setMasterItems(d?.items || []));
  }, [token, form.center, showDlg]);
  // Centre (country) changed after a package was picked → re-apply that country's package price and service rates
  const centreRef = useRef({ id: null, center: null });
  useEffect(() => {
    const key = editing?.id || "new";
    const prev = centreRef.current; centreRef.current = { id: key, center: form.center };
    if (prev.id !== key || prev.center === form.center || prev.center === null) return;
    if (!webMode || !curPkg) return;
    setForm((f) => ({ ...f, per_person_rate: pkgPrice(curPkg, isIntl),
      crockery_service: f.crockery_service ? { ...f.crockery_service, rate: rates.crockery } : null,
      staff_service: f.staff_service ? { ...f.staff_service, rate: rates.staff } : null }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.center, editing?.id]);
  const pickPkg = (p) => setForm((f) => {
    const trimmed = Object.fromEntries(SECTIONS.map(([, req]) => [req, (f.menu_selections?.[req] || []).slice(0, p.requirements?.[req] || 0)]).filter(([, v]) => v.length));
    return { ...f, gst_mode: "exclusive", package_id: p.id, package_name: p.name, per_person_rate: pkgPrice(p, isIntl), menu_selections: trimmed,
      crockery_service: f.crockery_service ? { ...f.crockery_service, rate: rates.crockery } : null, staff_service: f.staff_service ? { ...f.staff_service, rate: rates.staff } : null };
  });
  const money = (v) => `${currency}${Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const addExtra = (m) => setForm((f) => {
    const rate = Number(m.prices?.[f.center] || 0);
    return { ...f, add_ons: [...(f.add_ons || []), { name: m.name, qty: 1, rate, amount: rate }] };
  });

  return (
    <div className="space-y-6" data-testid="catering-orders-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-primary flex items-center gap-2">
            <ChefHat className="w-7 h-7" />Catering Orders
          </h1>
          <p className="text-muted-foreground text-sm">
            Website packages &amp; menu, live quotation (GST added on top). Older quotes keep their GST-included totals.
          </p>
        </div>
        <Button className="h-12 bg-[#123b8a] px-6 text-base hover:bg-[#0a2360]" onClick={() => open(null)} data-testid="catering-new-btn">
          <Plus className="w-4 h-4 mr-2" /> New Catering Enquiry
        </Button>
      </div>

      <Card><CardContent className="p-4 grid grid-cols-2 md:grid-cols-5 gap-3">
        {isAdmin && (
          <div><Label className="text-xs">Center</Label>
            <Select value={filters.center || "all"} onValueChange={(v) => setFilters(f => ({ ...f, center: v === "all" ? "" : v }))}>
              <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Centers</SelectItem>
                {centers.map(c => <SelectItem key={c.code} value={c.code}>{c.code}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
        <div><Label className="text-xs">From</Label>
          <Input className="h-9" type="date" value={filters.from_date}
            onChange={(e) => setFilters(f => ({ ...f, from_date: e.target.value }))} /></div>
        <div><Label className="text-xs">To</Label>
          <Input className="h-9" type="date" value={filters.to_date}
            onChange={(e) => setFilters(f => ({ ...f, to_date: e.target.value }))} /></div>
        <div><Label className="text-xs">Status</Label>
          <Select value={filters.status || "all"} onValueChange={(v) => setFilters(f => ({ ...f, status: v === "all" ? "" : v }))}>
            <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              {STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div><Label className="text-xs">Search</Label>
          <Input className="h-9" placeholder="Name / Phone / ID..." value={filters.search}
            onChange={(e) => setFilters(f => ({ ...f, search: e.target.value }))} />
        </div>
      </CardContent></Card>

      <Card><CardContent className="p-0">
        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin" /></div>
        ) : rows.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">No catering orders.</div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>ID</TableHead><TableHead>Customer</TableHead>
                <TableHead>Event</TableHead><TableHead>Guests</TableHead>
                <TableHead>Total</TableHead><TableHead>Status</TableHead>
                <TableHead>Center</TableHead><TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id} data-testid={`catering-row-${r.id}`}>
                  <TableCell className="text-xs">{r.id}</TableCell>
                  <TableCell>
                    <div className="font-medium">{r.customer_name}</div>
                    <div className="text-xs text-muted-foreground">{r.phone}</div>
                  </TableCell>
                  <TableCell className="text-xs">{r.event_date}<br />{r.event_time}<br />{r.occasion}</TableCell>
                  <TableCell>{r.guest_count}</TableCell>
                  <TableCell>{Number(r.total || 0).toFixed(2)}</TableCell>
                  <TableCell>
                    <Badge variant="outline">{r.confirmation_status}</Badge>
                    <div className="text-xs mt-1">{r.payment_status}</div>
                    <div className="mt-1"><PaymentStatusChip st={payMap[r.id]} testid={`catering-pay-status-${r.id}`} /></div>
                  </TableCell>
                  <TableCell className="text-xs">{r.center}</TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button size="icon" variant="ghost" onClick={() => open(r)} title="Edit"><Edit className="w-4 h-4" /></Button>
                      <button type="button" className="bi-pill !py-1 !px-2 !text-xs whitespace-nowrap" onClick={() => setPayRow(r)} data-testid={`catering-pay-btn-${r.id}`}>{actionLabel(payMap[r.id])}</button>
                      <Button size="icon" variant="ghost" onClick={() => downloadPdf(r)} title="Download PDF"><Download className="w-4 h-4" /></Button>
                      <Button size="icon" variant="ghost" onClick={() => sendQuote(r)} title="Send quote to customer" data-testid={`catering-send-quote-${r.id}`}>
                        {r.quote_sent_at
                          ? <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                          : <Mail className="w-4 h-4 text-blue-500" />}
                      </Button>
                      <Button size="icon" variant="ghost" onClick={() => setWaRow(r)} title="WhatsApp"
                        data-testid={`catering-whatsapp-${r.id}`}
                        className={r.whatsapp_sent_at ? "text-emerald-500" : "text-green-500"}>
                        <MessageSquare className="w-4 h-4" />
                      </Button>
                      <Button size="icon" variant="ghost" onClick={() => openMemoryBox(r)}
                        title="Create Memory Box for this guest"
                        data-testid={`catering-memory-box-${r.id}`}
                        className="text-rose-700 hover:text-rose-900">
                        <BookHeart className="w-4 h-4" />
                      </Button>
                      {isAdmin && <Button size="icon" variant="ghost" onClick={() => del(r)}><Trash2 className="w-4 h-4 text-red-500" /></Button>}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent></Card>

      {/* Form */}
      <BookingPaymentDialog open={!!payRow} onClose={() => { setPayRow(null); load(); }} session={session} bookingType="CATERING" bookingId={payRow?.id} onChanged={reloadPay} />
      <Dialog open={showDlg} onOpenChange={setShowDlg}>
        <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="catering-dialog">
          <DialogHeader><DialogTitle>{editing ? `Edit ${editing.id}` : "New Catering Enquiry"}</DialogTitle></DialogHeader>
          <div className="space-y-4" style={{ "--bi-accent": "#123b8a" }}>
            {!webMode && (
              <p className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900" data-testid="catering-legacy-note">
                This quote was made before website packages were added (GST included). Picking a package below switches it to the website quotation, with GST added on top.</p>
            )}
            <section className="bi-step" data-testid="catering-step-package">
              <h3 className="bi-step-title"><span className="bi-step-num">1</span>Select package</h3>
              {web ? <PackageCards packages={webPkgs} intl={isIntl} sym={currency} value={form.package_id} onPick={pickPkg} />
                : <p className="text-sm text-muted-foreground" data-testid="catering-pkg-loading">Loading website packages…</p>}
              {web?.source === "cache" && <p className="mt-1 text-xs text-amber-800">Website unreachable — showing the last saved copy ({(web.fetched_at || "").slice(0, 10)}).</p>}
            </section>
            <section className="bi-step" data-testid="catering-step-food">
              <h3 className="bi-step-title"><span className="bi-step-num">2</span>Select food</h3>
              {webMode && web && <MenuSelector pkg={curPkg} menu={web.menuOptions} sel={form.menu_selections || {}} onChange={(sel) => setForm(f => ({ ...f, menu_selections: sel }))} />}
              <div className="mt-3 space-y-3">
                {!webMode && <>
                  <div className="text-sm text-muted-foreground">Pick dishes from the Menu Master or type a custom item.</div>
                  {MENU_CATS.map((cat) => (
                    <MenuPicker key={cat}
                      category={cat}
                      rows={form.menu_categories[cat] || []}
                      onChange={(rows) => setForm(f => ({
                        ...f, menu_categories: { ...f.menu_categories, [cat]: rows },
                      }))}
                      master={masterByCat[cat] || []}
                      testIdPrefix={`catering-${cat.replace(/[^a-z]/gi, "")}`} />
                  ))}
                  <MenuPicker category="Beverages"
                    rows={form.beverages}
                    onChange={(rows) => setForm(f => ({ ...f, beverages: rows }))}
                    master={masterByCat["Beverage"] || []}
                    testIdPrefix="catering-bev" />
                </>}
                {webMode && (
                  <div className="flex flex-wrap items-center gap-2 rounded-xl border-2 border-[#ead6a6] bg-white p-3" data-testid="catering-master-search">
                    <span className="text-base font-bold text-[#750817]">Add extra dish from Menu Master:</span>
                    <MasterSearch menu={masterItems} center={form.center} country={isIntl ? "Australia" : "India"} value="" label=""
                      onPick={addExtra} onCustom={() => setForm((f) => ({ ...f, add_ons: [...(f.add_ons || []), { name: "", qty: 1, rate: 0, amount: 0 }] }))} testid="catering-master-picker" />
                    <span className="text-xs text-[#5a3a22]">Charged extra at the centre's approved Menu Master price × quantity.</span>
                  </div>
                )}
                <MenuPicker category={webMode ? "Extra dishes from Menu Master (paid add-on)" : "Add-ons"}
                  rows={form.add_ons}
                  onChange={(rows) => setForm(f => ({ ...f, add_ons: rows }))}
                  master={webMode ? [] : (masterByCat["Add-on"] || [])}
                  testIdPrefix="catering-addon" />
              </div>
            </section>

            <section className="bi-step" data-testid="catering-step-quote">
              <h3 className="bi-step-title"><span className="bi-step-num">3</span>Guests, services &amp; quotation</h3>
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-3 text-base">
                  <div className="grid grid-cols-2 gap-2">
                    <div><Label>Number of guests *</Label><Input type="number" value={form.guest_count} onChange={(e) => setForm(f => ({ ...f, guest_count: Number(e.target.value) || 0 }))} data-testid="catering-guests" /></div>
                    {webMode ? <div><Label>Price per person</Label><div className="h-10 flex items-center font-bold" data-testid="catering-pp-rate">{money(form.per_person_rate)}</div></div>
                      : <div><Label>Price per person</Label><Input type="number" value={form.per_person_rate} onChange={(e) => setForm(f => ({ ...f, per_person_rate: Number(e.target.value) || 0 }))} data-testid="catering-pp-rate" /></div>}
                  </div>
                  {webMode && web && <ServicesPicker form={form} setForm={setForm} rates={rates} terms={web.service_terms} sym={currency} />}
                  <div className="grid grid-cols-2 gap-2">
                    <div><Label>Delivery / transport charges</Label>
                      <Input type="number" value={form.transport_charges} onChange={(e) => setForm(f => ({ ...f, transport_charges: Number(e.target.value) || 0 }))} data-testid="catering-transport" /></div>
                    {webMode && <div><Label>Approved discount</Label>
                      <Input type="number" value={form.discount} onChange={(e) => setForm(f => ({ ...f, discount: Number(e.target.value) || 0 }))} data-testid="catering-discount" /></div>}
                  </div>
                </div>
                {webMode ? (
                  <div className="rounded-2xl border-[3px] border-[#d9ae52] bg-gradient-to-br from-[#fffaf0] to-[#fbf0d6] p-4 space-y-1 text-base" data-testid="catering-live-quote">
                    <h4 className="text-lg font-bold text-[#750817]">{form.package_name || "Select a package"}</h4>
                    <div className="flex justify-between"><span>{form.guest_count} guests × {money(form.per_person_rate)}</span><b data-testid="catering-food-total">{money(form.food_total)}</b></div>
                    {form.staff_service && <div className="flex justify-between"><span>Service staff: {form.staff_service.count} × {form.staff_service.hours} hr × {money(form.staff_service.rate)}</span><b data-testid="catering-staff-total">{money(form.staff_service.total)}</b></div>}
                    {form.crockery_service && <div className="flex justify-between"><span>Crockery: {form.crockery_service.hours} hr × {money(form.crockery_service.rate)}</span><b data-testid="catering-crockery-total">{money(form.crockery_service.total)}</b></div>}
                    {(form.add_ons || []).length > 0 && <div className="flex justify-between"><span>Extra dishes</span><b>{money((form.add_ons || []).reduce((a, r) => a + (Number(r.amount) || 0), 0))}</b></div>}
                    {Number(form.transport_charges) > 0 && <div className="flex justify-between"><span>Delivery / transport</span><b>{money(form.transport_charges)}</b></div>}
                    {Number(form.discount) > 0 && <div className="flex justify-between text-emerald-800"><span>Approved discount</span><b>−{money(form.discount)}</b></div>}
                    <div className="flex justify-between border-t border-[#d9ae52] pt-1"><span>Subtotal before taxes</span><b data-testid="catering-subtotal">{money(form.subtotal)}</b></div>
                    <div className="flex justify-between"><span>GST {form.gst_rate}% (added)</span><b data-testid="catering-gst">{money(form.gst)}</b></div>
                    <div className="flex justify-between text-xl text-[#750817]"><span className="font-bold">Estimated quotation</span><b data-testid="catering-total">{money(form.total)}</b></div>
                  </div>
                ) : (
                  <CostSummary sym={currency} testid="catering-cost"
                    rows={[["Food + charges (before GST)", form.subtotal, "catering-subtotal"], [gstLabel, form.gst, "catering-gst"]]}
                    total={["Estimated final amount", form.total]} />
                )}
              </div>
              {!webMode && <div className="text-xs text-muted-foreground italic mt-2">GST is included in the total (same calculation as before: {isIntl ? "Australia 10%" : "India 5%"}).</div>}
            </section>

            <section className="bi-step" data-testid="catering-step-customer">
              <h3 className="bi-step-title"><span className="bi-step-num">4</span>Customer details</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><Label>Customer name *</Label><Input value={form.customer_name} onChange={(e) => setForm(f => ({ ...f, customer_name: e.target.value }))} data-testid="catering-customer-name" /></div>
                <div><Label>Phone *</Label><Input value={form.phone} onChange={(e) => setForm(f => ({ ...f, phone: e.target.value }))} data-testid="catering-phone" /></div>
                <div><Label>Event date *</Label><Input type="date" value={form.event_date} onChange={(e) => setForm(f => ({ ...f, event_date: e.target.value }))} data-testid="catering-event-date" /></div>
                <div><Label>Event time</Label><Input value={form.event_time} placeholder="7:00 PM" onChange={(e) => setForm(f => ({ ...f, event_time: e.target.value }))} data-testid="catering-event-time" /></div>
                {isAdmin && (
                  <div>
                    <Label>Centre *</Label>
                    <Select value={form.center} onValueChange={(v) => setForm(f => ({ ...f, center: v }))}>
                      <SelectTrigger data-testid="catering-centre"><SelectValue placeholder="Pick centre..." /></SelectTrigger>
                      <SelectContent>{centers.map(c => <SelectItem key={c.code} value={c.code}>{c.code}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                )}
                <div><Label>Email (optional)</Label><Input value={form.email} onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))} /></div>
                <div className="sm:col-span-2"><Label>Venue / delivery address (if delivering)</Label><Textarea rows={2} value={form.delivery_address} onChange={(e) => setForm(f => ({ ...f, delivery_address: e.target.value }))} /></div>
              </div>
            </section>

            <details className="bi-step" data-testid="catering-more">
              <summary className="cursor-pointer text-base font-semibold text-[#123b8a]">More details (billing, staffing, payment, notes)</summary>
              <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="sm:col-span-2"><Label>Billing address</Label><Textarea rows={2} value={form.billing_address} onChange={(e) => setForm(f => ({ ...f, billing_address: e.target.value }))} /></div>
                <div><Label>Order date</Label><Input type="date" value={form.order_date} onChange={(e) => setForm(f => ({ ...f, order_date: e.target.value }))} /></div>
                <div><Label>Occasion</Label><Input value={form.occasion} onChange={(e) => setForm(f => ({ ...f, occasion: e.target.value }))} placeholder="Wedding, Birthday..." /></div>
                <div><Label>Coordinator</Label><Input value={form.assigned_coordinator} onChange={(e) => setForm(f => ({ ...f, assigned_coordinator: e.target.value }))} /></div>
                <div><Label>Advance paid</Label><Input type="number" value={form.advance_paid} onChange={(e) => setForm(f => ({ ...f, advance_paid: Number(e.target.value) || 0 }))} /></div>
                <div><Label>Crockery / logistics</Label><Textarea rows={2} value={form.crockery} onChange={(e) => setForm(f => ({ ...f, crockery: e.target.value }))} /></div>
                <div><Label>Staffing / service</Label><Textarea rows={2} value={form.staffing} onChange={(e) => setForm(f => ({ ...f, staffing: e.target.value }))} /></div>
                <div><Label>Confirmation status</Label>
                  <Select value={form.confirmation_status} onValueChange={(v) => setForm(f => ({ ...f, confirmation_status: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Payment status</Label>
                  <Select value={form.payment_status} onValueChange={(v) => setForm(f => ({ ...f, payment_status: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{PAY_STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="flex justify-between text-base sm:col-span-2"><span>Balance due</span><b>{currency}{form.balance_due.toFixed(2)}</b></div>
                <div className="sm:col-span-2"><Label>Quote notes</Label><Textarea rows={2} value={form.quote_notes} onChange={(e) => setForm(f => ({ ...f, quote_notes: e.target.value }))} /></div>
                <div className="sm:col-span-2"><Label>Internal remarks</Label><Textarea rows={2} value={form.remarks} onChange={(e) => setForm(f => ({ ...f, remarks: e.target.value }))} /></div>
              </div>
            </details>
          </div>
          <DialogFooter className="flex-wrap gap-2">
            <Button variant="outline" onClick={() => setShowDlg(false)}>Cancel</Button>
            {editing && <Button variant="outline" onClick={() => downloadPdf(editing)} data-testid="catering-draft-pdf"><Download className="w-4 h-4 mr-2" />Download draft PDF</Button>}
            {editing && <Button variant="outline" className="text-green-700" onClick={() => { setShowDlg(false); setWaRow(editing); }} data-testid="catering-share-wa"><MessageSquare className="w-4 h-4 mr-2" />Share on WhatsApp</Button>}
            <Button onClick={save} data-testid="catering-save-btn">{editing ? "Save changes" : "Save Enquiry"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <WhatsAppDialog
        open={!!waRow}
        onOpenChange={(v) => !v && setWaRow(null)}
        kind="catering"
        row={waRow}
        token={token}
        onMarkedSent={load}
      />
    </div>
  );
}
